import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    ButtonBase,
    Chip,
    IconButton,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './CambioTurnoSelector.css';

function inicioDeMes(fecha) {
    return new Date(fecha.getFullYear(), fecha.getMonth(), 1);
}

function mismaFechaMes(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

function claveDia(fecha) {
    return [
        fecha.getFullYear(),
        String(fecha.getMonth() + 1).padStart(2, '0'),
        String(fecha.getDate()).padStart(2, '0'),
    ].join('-');
}

function formatearMes(fecha) {
    return fecha.toLocaleDateString('es-AR', {
        month: 'long',
        year: 'numeric',
    });
}

function formatearDiaCorto(fecha) {
    return fecha.toLocaleDateString('es-AR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
    });
}

function formatearHora(fecha) {
    return fecha.toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

function obtenerDiasCalendario(mesVisible) {
    const primerDiaMes = inicioDeMes(mesVisible);
    const ultimoDiaMes = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 0);
    const primerDiaGrilla = new Date(primerDiaMes);
    const offsetLunes = (primerDiaMes.getDay() + 6) % 7;

    primerDiaGrilla.setDate(primerDiaMes.getDate() - offsetLunes);

    const totalDias = Math.ceil((offsetLunes + ultimoDiaMes.getDate()) / 7) * 7;

    return Array.from({ length: totalDias }, (_, index) => {
        const fecha = new Date(primerDiaGrilla);
        fecha.setDate(primerDiaGrilla.getDate() + index);
        return fecha;
    });
}

function agruparPorClaveDia(turnos) {
    const grupos = new Map();

    turnos.forEach(turno => {
        const fecha = new Date(turno.fechaHora);
        const key = claveDia(fecha);

        if (!grupos.has(key)) {
            grupos.set(key, {
                key,
                fecha,
                turnos: [],
            });
        }

        grupos.get(key).turnos.push(turno);
    });

    return Array.from(grupos.values()).map(grupo => ({
        ...grupo,
        turnos: grupo.turnos.sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora)),
    }));
}

function CambioTurnoSelector({ turnos, seleccionado, onSelect }) {
    const [mesVisible, setMesVisible] = useState(() => inicioDeMes(new Date()));
    const [diaSeleccionado, setDiaSeleccionado] = useState(null);

    useEffect(() => {
        if (turnos.length === 0) return;

        const primerTurno = [...turnos].sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora))[0];
        const fechaPrimerTurno = new Date(primerTurno.fechaHora);

        setMesVisible(inicioDeMes(fechaPrimerTurno));
        setDiaSeleccionado(claveDia(fechaPrimerTurno));
    }, [turnos]);

    useEffect(() => {
        if (!seleccionado) return;

        const fecha = new Date(seleccionado.fechaHora);
        setMesVisible(inicioDeMes(fecha));
        setDiaSeleccionado(claveDia(fecha));
    }, [seleccionado]);

    const turnosPorDia = useMemo(() => {
        return agruparPorClaveDia(turnos).reduce((acc, grupo) => {
            acc[grupo.key] = grupo;
            return acc;
        }, {});
    }, [turnos]);

    const diasCalendario = useMemo(() => obtenerDiasCalendario(mesVisible), [mesVisible]);
    const turnosDelDia = diaSeleccionado ? turnosPorDia[diaSeleccionado]?.turnos ?? [] : [];

    function cambiarMes(offset) {
        const proximoMes = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + offset, 1);
        const primerDiaDisponible = Object.values(turnosPorDia)
            .filter(grupo => mismaFechaMes(grupo.fecha, proximoMes))
            .sort((a, b) => a.fecha - b.fecha)[0];

        setMesVisible(proximoMes);
        setDiaSeleccionado(primerDiaDisponible?.key ?? null);
    }

    return (
        <Box className="cambio-turno">
            <Box className="cambio-turno__mes">
                <IconButton
                    onClick={() => cambiarMes(-1)}
                    aria-label="Mes anterior"
                    className="cambio-turno__nav"
                >
                    <ChevronLeftIcon />
                </IconButton>

                <Typography
                    component="strong"
                    className="cambio-turno__titulo-mes"
                >
                    {formatearMes(mesVisible)}
                </Typography>

                <IconButton
                    onClick={() => cambiarMes(1)}
                    aria-label="Mes siguiente"
                    className="cambio-turno__nav"
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>

            <Box className="cambio-turno__contenido">
                <Box className="cambio-turno__cal-panel">
                    <Box className="cambio-turno__semana">
                        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(dia => (
                            <Typography key={dia} component="span">
                                {dia}
                            </Typography>
                        ))}
                    </Box>

                    <Box className="cambio-turno__calendario">
                        {diasCalendario.map(fecha => {
                            const key = claveDia(fecha);
                            const grupo = turnosPorDia[key];
                            const disponible = Boolean(grupo);
                            const fueraDeMes = !mismaFechaMes(fecha, mesVisible);
                            const activo = diaSeleccionado === key;

                            return (
                                <ButtonBase
                                    key={key}
                                    className={[
                                        'cambio-turno__fecha',
                                        disponible ? 'cambio-turno__fecha--disponible' : '',
                                        fueraDeMes ? 'cambio-turno__fecha--fuera' : '',
                                        activo ? 'cambio-turno__fecha--activa' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => disponible && !fueraDeMes && setDiaSeleccionado(key)}
                                    disabled={!disponible || fueraDeMes}
                                    aria-pressed={activo}
                                >
                                    <Typography component="span" className="cambio-turno__fecha-numero">
                                        {fecha.getDate()}
                                    </Typography>
                                    {disponible && (
                                        <Chip
                                            label={grupo.turnos.length}
                                            size="small"
                                            className="cambio-turno__contador"
                                        />
                                    )}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                <Box className="cambio-turno__horarios-dia">
                    {turnosDelDia.length === 0 ? (
                        <Typography className="cambio-turno__vacio">
                            Elegí un día disponible.
                        </Typography>
                    ) : (
                        <>
                            <Typography
                                component="h4"
                                className="cambio-turno__dia-titulo"
                            >
                                {formatearDiaCorto(new Date(turnosDelDia[0].fechaHora))}
                            </Typography>

                            <Box className="cambio-turno__horas">
                                {turnosDelDia.map(turno => {
                                    const fecha = new Date(turno.fechaHora);
                                    const estaSeleccionado = seleccionado?._id === turno._id;

                                    return (
                                        <Button
                                            key={turno._id}
                                            variant="outlined"
                                            className={`cambio-turno__hora ${estaSeleccionado ? 'cambio-turno__hora--seleccionada' : ''}`}
                                            onClick={() => onSelect(turno)}
                                        >
                                            {formatearHora(fecha)}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default CambioTurnoSelector;
